import {decodeBase64BigEndian, encodeBase64BigEndian} from "../util/binary";
import cookies from "services-comun/modules/browser/cookies";
import {CMP_ADD_CONSENT_COOKIE, CMP_ADD_CONSENT_LOCALKEY} from "../cmp/cmp";

interface IVendorInfo {
    [key:number]: boolean|undefined;
}

class AddtlConsent {
    /* STATIC */
    private static REGEX: RegExp = /[12]~([\d.]+)?(?:~dv.)?([\d.]+)?/;

    private static VERSION: number = 2;

    public static getString(): string|undefined {
        if (localStorage) {
            return localStorage.getItem(CMP_ADD_CONSENT_LOCALKEY)||undefined;
        }
        return cookies.get(CMP_ADD_CONSENT_COOKIE);
    }

    public static buildProxy(inputString?: string): AddtlConsent {
        const addtlConsent: AddtlConsent = new AddtlConsent();
        if (inputString) {
            addtlConsent.encodedStringExists = true;

            const match: RegExpMatchArray|null = inputString.match(AddtlConsent.REGEX);

            if (match) {
                const acceptedVendors: string|undefined = match[1];
                const rejectedVendors: string|undefined = match[2];
                if (acceptedVendors) {
                    acceptedVendors.split('.').filter(v => v != "").forEach(vendorId => {
                        addtlConsent.vendorInfo[parseInt(vendorId)] = true;
                    });
                }
                if (rejectedVendors) {
                    rejectedVendors.split('.').filter(v => v != "").forEach(vendorId => {
                        addtlConsent.vendorInfo[parseInt(vendorId)] = false;
                    });
                }
            } else {
                const binaryString: string = decodeBase64BigEndian(inputString);
                const binaryStringLen: number = binaryString.length;
                for (let vendorId: number = 1; vendorId <= binaryStringLen; vendorId++) {
                    const selected: boolean = binaryString.charAt(vendorId - 1) == '1';
                    if (selected) addtlConsent.vendorInfo[vendorId] = true;
                }
            }
        }

        addtlConsent.encodedStringExists = inputString !== undefined;
        return addtlConsent;
    }

    /* INSTANCE */

    private encodedStringExists: boolean = false;
    private vendorInfo: IVendorInfo;

    private constructor() {
        this.vendorInfo = {};
    }

    public update(acceptedVendorsIds: number[], rejectedVendorsIds: number[]) : void {
        this.vendorInfo = {};
        acceptedVendorsIds.forEach(vendorId => {this.vendorInfo[vendorId] = true;});
        rejectedVendorsIds.forEach(vendorId => {this.vendorInfo[vendorId] = false;});
    }

    /**
     * Codifica en base64 la cadena binaria.
     * Sólo se codifican los vendors aceptados.
     * @private
     * @return Cadena en base64 con la cadena binaria.
     */
    private encode() : string {
        let acceptedVendors: number[] = Object.keys(this.vendorInfo).map(parseInt).filter(v => this.vendorInfo[v] === true);
        const maxId = Math.max(...acceptedVendors, 0)
        let addtlString: string = '';
        for (let vendorId: number=1; vendorId <= maxId; vendorId++) {
            addtlString += this.vendorInfo[vendorId] ? '1' : '0';
        }
        return encodeBase64BigEndian(addtlString);
    }

    /**
     * Genera la cadena decodificada.
     * @private
     * @return cadena decodificada.
     */
    public decodedString() : string {
        let addtlString: string = `${AddtlConsent.VERSION}~`;
        let acceptedVendors: number[] = Object.keys(this.vendorInfo).map(v => parseInt(v)).filter(v => this.vendorInfo[v] === true);
        if (acceptedVendors.length > 0) {
            addtlString += acceptedVendors.join('.');
        }

        let rejectedVendors: number[] = Object.keys(this.vendorInfo).map(v => parseInt(v)).filter(v => this.vendorInfo[v] === false);
        if (rejectedVendors.length > 0) {
            addtlString += `~dv.${rejectedVendors.join('.')}`;
        }
        return addtlString;
    }

    /**
     * Genera la cadena codificada.
     */
    public encodedString() : string {
        return this.encode();
    }

    /**
     * Persiste la información de consentimiento adicional.
     * @return True si se ha guardado correctamente, False en otro caso.
     */
    public save() : boolean {
        if (localStorage) {
            this.loadLocalStorage();
        } else {
            cookies.set(CMP_ADD_CONSENT_COOKIE, this.encode()||' ', new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)));
        }
        this.encodedStringExists = true;
        return true;
    }

    /**
     * Carga la información de consentimiento en el localStorage decodificada.
     * @private
     */
    private loadLocalStorage() : void {
        localStorage.setItem(CMP_ADD_CONSENT_LOCALKEY, this.decodedString());
    }

    public acceptedVendorsIds() : number[] {
        return Object.keys(this.vendorInfo).map(v => parseInt(v)).filter(v => this.vendorInfo[v] === true);
    }

    public rejectedVendorsIds() : number[] {
        return Object.keys(this.vendorInfo).map(v => parseInt(v)).filter(v => this.vendorInfo[v] === false);
    }

    public nonDisclosedVendorsIds() : number[] {
        return Object.keys(this.vendorInfo).map(v => parseInt(v)).filter(v => this.vendorInfo[v] === undefined);
    }

    public vendorsIds() : number[] {
        return [
            ...this.acceptedVendorsIds(),
            ...this.rejectedVendorsIds(),
            ...this.nonDisclosedVendorsIds()
        ];
    }

    public vendors(): IVendorInfo {
        return this.vendorInfo;
    }

    public existsMeteoredAddtlCookie(){
        return this.encodedStringExists;
    }

}

export {AddtlConsent}
